import { type ComponentType, type ReactNode, useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SectionCompleteBadge } from '@/components/atoms';
import { SectionHeader, TruncateLevelsDialog } from '@/components/molecules';
import {
  AppTopbar,
  ConfirmDialog,
  MobileAsideDrawer,
  StepNav,
  StepRail,
  type StepRailSection,
  type StepRailStatus,
} from '@/components/organisms';
import { AppShell, Aside } from '@/components/templates';
import { dwrClassesById } from '@/data/classes';
import type { Character } from '@/schemas/character';
import type { ClassDefinition } from '@/schemas/classDefinition';
import { BuilderProvider } from '../contexts/BuilderContext';
import { useBuilderDraft, useResetBuilder } from '../contexts/useBuilder';
import { useBuilderEditHandlers } from '../hooks/useBuilderEditHandlers';
import { useCurrentBuilderSection } from '../hooks/useCurrentBuilderSection';
import { useFinalizeBuilder } from '../hooks/useFinalizeBuilder';
import { isSectionComplete } from '../reducer';
import { getSectionMissingHint } from '../services/sectionMissingHint';
import { characterToDraft } from '../services/characterToDraft';
import {
  scrollToBuilderSection,
  scrollToPrevBuilderSection,
} from '../services/builderScrollHelpers';
import { rewindToLevel } from '@/services/characterRewind';
import { SectionAlignment } from '../sections/SectionAlignment';
import { SectionArchetype } from '../sections/SectionArchetype';
import { SectionAspetti } from '../sections/SectionAspetti';
import { SectionClass } from '../sections/SectionClass';
import { SectionEquipment } from '../sections/SectionEquipment';
import { SectionIdentityBonds } from '../sections/SectionIdentityBonds';
import { SectionRace } from '../sections/SectionRace';
import { SectionSpellSelection } from '../sections/SectionSpellSelection';
import { SectionStartingMoves } from '../sections/SectionStartingMoves';
import { SectionStats } from '../sections/SectionStats';
import type { BuilderSectionId, CharacterDraft } from '../types';

// page — DWR Character Builder. Rewrite cosmetico Fase 2 PR 3:
// layout AppShell + AppTopbar variant="default" + StepRail aside + StepNav sticky.
// Le 10 Section* interne restano invariate (D3 del WIP): SectionHeader + body + badge
// sono orchestrati qui. Scroll-spy via useSectionInView aggiorna lo stato "current"
// di StepRail e StepNav mentre l'utente scrolla.

interface SectionDef {
  id: BuilderSectionId;
  stepNumber: string;
  label: string;
  title: string;
  sub: ReactNode;
  Component: ComponentType;
}

const SECTIONS: readonly SectionDef[] = [
  {
    id: 'class',
    stepNumber: '01',
    label: 'Classe',
    title: 'Scegli la tua classe',
    sub: 'Il libretto che porterai per tutta la storia del personaggio. Definisce mosse, dado di danno, equipaggiamento e modo di entrare nella narrazione.',
    Component: SectionClass,
  },
  {
    id: 'race',
    stepNumber: '02',
    label: 'Razza',
    title: 'Scegli la razza',
    sub: 'Le razze suggerite dal manuale, o una tua proposta da concordare con il GM.',
    Component: SectionRace,
  },
  {
    id: 'archetype',
    stepNumber: '03',
    label: 'Archetipo',
    title: 'Scegli il tuo archetipo',
    sub: 'Una specializzazione della classe che modifica HP, mosse e progressione.',
    Component: SectionArchetype,
  },
  {
    id: 'stats',
    stepNumber: '04',
    label: 'Caratteristiche',
    title: 'Distribuisci le caratteristiche',
    sub: (
      <>
        Assegna i sei punteggi standard <strong>16, 15, 13, 12, 9, 8</strong> alle sei
        caratteristiche.
      </>
    ),
    Component: SectionStats,
  },
  {
    id: 'alignment',
    stepNumber: '05',
    label: 'Allineamento',
    title: "Scegli l'allineamento",
    sub: 'Quando agisci secondo il tuo allineamento durante una sessione, segni un PE.',
    Component: SectionAlignment,
  },
  {
    id: 'aspetti',
    stepNumber: '06',
    label: 'Aspetti',
    title: 'Aspetti',
    sub: 'Tratti narrativi che ti distinguono — variano per classe.',
    Component: SectionAspetti,
  },
  {
    id: 'equipment',
    stepNumber: '07',
    label: 'Equipaggiamento',
    title: 'Equipaggiamento iniziale',
    sub: "Quello con cui parti all'avventura.",
    Component: SectionEquipment,
  },
  {
    id: 'starting-moves',
    stepNumber: '08',
    label: 'Mosse iniziali',
    title: 'Mosse iniziali',
    sub: 'Le tue mosse iniziali dipendono dalla classe scelta.',
    Component: SectionStartingMoves,
  },
  {
    id: 'spells',
    stepNumber: '09',
    label: 'Incantesimi',
    title: 'Incantesimi',
    sub: 'Disponibile solo se conosci Lanciare Incantesimi Arcani / Divini / del Patrono.',
    Component: SectionSpellSelection,
  },
  {
    id: 'identity-bonds',
    stepNumber: '10',
    label: 'Identità',
    title: 'Identità e Legami',
    sub: 'Il nome, il titolo, e i legami che hai con gli altri personaggi del gruppo.',
    Component: SectionIdentityBonds,
  },
];

const RAIL_SECTIONS: readonly StepRailSection[] = SECTIONS.map((s) => ({
  id: s.id,
  label: s.label,
  stepNumber: s.stepNumber,
}));

interface BuilderPageProps {
  /**
   * **FI-46bis** — Se passato, attiva la modalità **edit retroattivo L1**:
   *  - `BuilderProvider` montato con `initialDraft = characterToDraft(rewindToLevel(c, 1))`
   *    e `persistDraft={false}` (no autosave)
   *  - `useFinalizeBuilder` riceve `editFromCharacter` → preserva id/createdAt + UndoToast
   *  - `<TruncateLevelsDialog>` mostrato al Finalizza se `editFromCharacter.level > 1`
   *  - Cancel link "← Hub" mostra `ConfirmDialog` se ci sono modifiche pendenti
   *
   * In creazione normale (default) → undefined → behavior pre-FI-46bis.
   */
  editFromCharacter?: Character | undefined;
}

export function BuilderPage({ editFromCharacter }: BuilderPageProps = {}) {
  // FI-46bis: in edit-mode il draft iniziale è derivato dal PG L1 baseline
  // (via rewindToLevel + characterToDraft). Computato qui (fuori dal
  // Provider) per evitare re-compute ad ogni render.
  const initialDraft = editFromCharacter
    ? characterToDraft(rewindToLevel(editFromCharacter, 1))
    : undefined;
  return (
    <BuilderProvider initialDraft={initialDraft} persistDraft={editFromCharacter === undefined}>
      <BuilderPageInner editFromCharacter={editFromCharacter} />
    </BuilderProvider>
  );
}

function BuilderPageInner({ editFromCharacter }: BuilderPageProps) {
  const draft = useBuilderDraft();
  const classDef = draft.classId ? dwrClassesById[draft.classId] : undefined;
  const { canFinalize, handleFinalize } = useFinalizeBuilder(
    editFromCharacter !== undefined ? { editFromCharacter } : {},
  );
  const edit = useBuilderEditHandlers(editFromCharacter, handleFinalize);

  const { currentId, safeIndex, currentSection } = useCurrentBuilderSection(SECTIONS, draft.id);

  const getStatus = useCallback(
    (id: string): StepRailStatus =>
      isSectionComplete(id as BuilderSectionId, draft, classDef) ? 'done' : 'todo',
    [draft, classDef],
  );

  const handlePrev = (): void =>
    scrollToPrevBuilderSection(currentSection.id, SECTIONS[safeIndex - 1]?.id);

  const handleNext = (): void => {
    const next = SECTIONS[safeIndex + 1];
    if (next) {
      scrollToBuilderSection(next.id);
      return;
    }
    // Ultima sezione: se tutto il PG è completo, finalizza in 1 click. In
    // edit-mode L1 `performFinalize` apre prima `<TruncateLevelsDialog>`.
    if (canFinalize) edit.performFinalize();
  };

  const isLastSection = safeIndex === SECTIONS.length - 1;
  // Sull'ultima sezione il button "Finalizza" è abilitato solo se TUTTO
  // il PG è completo, non solo la sezione corrente.
  const stepNavComplete = isLastSection ? canFinalize : getStatus(currentSection.id) === 'done';

  // **FI-53 (Sprint F)**: la StepRail viene riusata in 2 slot — `<Aside>`
  // desktop (≥900px) e `<MobileAsideDrawer>` mobile (<900px). Estratta come
  // variabile per evitare duplicazione dei prop. Stesso watermark URL.
  const watermarkUrl = draft.classId ? `/classes/${draft.classId}.png` : undefined;
  const stepRail = (
    <StepRail
      sections={RAIL_SECTIONS}
      currentId={currentId}
      getStatus={getStatus}
      onJump={scrollToBuilderSection}
    />
  );

  return (
    <>
      <AppShell
        header={
          <AppTopbar
            variant="default"
            brand={<BuilderBrand mobileDrawerContent={stepRail} watermarkUrl={watermarkUrl} />}
            center={<BuilderCharLabel draft={draft} classLabel={classDef?.name} />}
            actions={<BuilderActions isEditMode={edit.isEditMode} />}
          />
        }
        main={
          <div className="flex flex-col">
            <StepNav
              currentIndex={safeIndex}
              total={SECTIONS.length}
              currentLabel={currentSection.label}
              isCurrentComplete={stepNavComplete}
              isLast={isLastSection}
              onPrev={handlePrev}
              onNext={handleNext}
            />
            {/* **FI-65** `key={draft.id}` forza re-mount totale al `RESET_DRAFT`
             *  (createEmptyDraft genera nuovo UUID). Le sezioni hanno useState
             *  locali derivati dal draft (es. `magoL1Selected` in
             *  SectionSpellSelection, `groupSelections` in SectionStartingMoves)
             *  che NON si re-inizializzano senza unmount. Durante editing
             *  normale (SET_*) l'id resta invariato → no re-mount, state
             *  preservato. Pattern coerente con React docs "Resetting state
             *  with a key". */}
            <SectionList key={draft.id} draft={draft} classDef={classDef ?? null} />
          </div>
        }
        aside={<Aside watermarkUrl={watermarkUrl}>{stepRail}</Aside>}
      />
      {edit.isEditMode ? (
        <TruncateLevelsDialog
          open={edit.truncateDialogOpen}
          onOpenChange={edit.setTruncateDialogOpen}
          onConfirm={edit.confirmTruncate}
          editLevel={1}
          truncatedLevels={edit.truncatedLevels}
        />
      ) : null}
    </>
  );
}

interface SectionListProps {
  draft: CharacterDraft;
  classDef: ClassDefinition | null;
}

function SectionList({ draft, classDef }: SectionListProps) {
  return (
    <>
      {SECTIONS.map((s) => {
        const done = isSectionComplete(s.id, draft, classDef ?? undefined);
        const hint = done
          ? undefined
          : (getSectionMissingHint(s.id, draft, classDef ?? undefined) ?? undefined);
        const Component = s.Component;
        return (
          <section key={s.id} id={`section-${s.id}`} className="mb-12 scroll-mt-32">
            <SectionHeader stepNumber={s.stepNumber} title={s.title} sub={s.sub} />
            <Component />
            <div className="mt-6">
              <SectionCompleteBadge done={done} hint={hint} />
            </div>
          </section>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Topbar sub-componenti (page-specific)
// ---------------------------------------------------------------------------

function BuilderBrand({
  mobileDrawerContent,
  watermarkUrl,
}: {
  mobileDrawerContent: ReactNode;
  watermarkUrl: string | undefined;
}) {
  return (
    <>
      {/* Mobile (<900px): drawer trigger. Sostituisce il quadrato DW.
       *  Su tap apre uno Sheet con la StepRail. **FI-53 Sprint F.** */}
      <span className="min-[900px]:hidden">
        <MobileAsideDrawer title="Navigazione sezioni" watermarkUrl={watermarkUrl}>
          {mobileDrawerContent}
        </MobileAsideDrawer>
      </span>
      {/* Desktop (≥900px): brand DW + nome app, invariato. */}
      <span
        className="bg-ink text-paper-bright font-display grid h-7 w-7 place-items-center text-sm font-bold tracking-normal max-[900px]:hidden"
        aria-hidden
      >
        DW
      </span>
      <span className="font-display text-[0.95rem] font-bold tracking-[0.08em] max-[900px]:hidden">
        Dungeon World <em className="font-normal italic opacity-[0.65]">Rebrewed</em>
      </span>
    </>
  );
}

interface BuilderCharLabelProps {
  draft: CharacterDraft;
  classLabel?: string | undefined;
}

function BuilderCharLabel({ draft, classLabel }: BuilderCharLabelProps) {
  if (!draft.name && !draft.classId) {
    return <em className="text-ink-soft text-[0.95rem]">Nuovo personaggio</em>;
  }
  return (
    <span className="text-ink-soft text-[0.95rem] italic">
      <strong className="text-ink font-semibold not-italic">{draft.name ?? 'Senza nome'}</strong>
      {draft.title ? <span> {draft.title}</span> : null}
      {classLabel ? <em>, {classLabel}</em> : null}
    </span>
  );
}

// page-specific, not reusable: ghost link/button per la BuilderTopbar — sarà
// generalizzato come <Chip variant="ghost" as="button"> in PR 4-7 di Fase 2
// (cluster .ghost-btn del mockup, come già notato in CharacterViewPage).
//
// **PR 11 follow-up**: aggiunto button "Reset" ghost-btn--danger pattern
// mockup chrome.jsx:22. Click → ConfirmDialog destructive → resetBuilder.
//
// **FI-46bis**: in edit-mode L1, il link "← Hub" diventa button con
// ConfirmDialog "Annullare modifiche?" (safety, il draft è pre-popolato
// e qualsiasi modifica andrebbe persa). Il button "Reset" è nascosto in
// edit-mode (semantica diversa: reset = ricreazione PG da zero, non
// applicabile a edit retroattivo).
function BuilderActions({ isEditMode = false }: { isEditMode?: boolean }) {
  const navigate = useNavigate();
  const resetBuilder = useResetBuilder();
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [cancelEditDialogOpen, setCancelEditDialogOpen] = useState(false);

  const hubLinkClass =
    'font-display border-ink-soft text-ink hover:bg-paper-soft border bg-transparent px-3 py-1.5 text-xs tracking-[0.08em] uppercase transition';

  return (
    <>
      {isEditMode ? (
        <button
          type="button"
          onClick={() => setCancelEditDialogOpen(true)}
          className={hubLinkClass}
          title="Torna alla scheda personaggio senza salvare le modifiche"
        >
          ← Hub
        </button>
      ) : (
        <Link
          to="/character"
          className={hubLinkClass}
          title="Torna all'Hub del personaggio (richiede un personaggio salvato)"
        >
          ← Hub
        </Link>
      )}
      {isEditMode ? null : (
        <button
          type="button"
          onClick={() => setResetDialogOpen(true)}
          className="font-display border bg-transparent px-3 py-1.5 text-xs tracking-[0.08em] uppercase transition border-[#a4421e] text-[#a4421e] hover:bg-[rgba(164,66,30,0.08)]"
          title="Cancella tutte le scelte e riparti da zero"
        >
          Reset
        </button>
      )}
      <ConfirmDialog
        open={resetDialogOpen}
        onOpenChange={setResetDialogOpen}
        variant="destructive"
        title="Resettare la creazione del personaggio?"
        description="Tutte le scelte fatte finora andranno perse. Ripartirai dalla scelta della classe."
        confirmLabel="Sì, resetta"
        cancelLabel="Annulla"
        onConfirm={() => {
          resetBuilder();
          setResetDialogOpen(false);
        }}
      />
      <ConfirmDialog
        open={cancelEditDialogOpen}
        onOpenChange={setCancelEditDialogOpen}
        variant="destructive"
        title="Annullare la modifica del livello 1?"
        description="Le modifiche fatte finora andranno perse. Il personaggio resta come prima."
        confirmLabel="Sì, annulla"
        cancelLabel="Resta qui"
        onConfirm={() => {
          setCancelEditDialogOpen(false);
          void navigate('/character');
        }}
      />
    </>
  );
}
